import { format, parseISO, differenceInDays, isPast, isToday, isValid, startOfDay } from 'date-fns';
import {
  Calendar,
  Users,
  Clock,
  TrendingUp,
  FolderKanban,
  Building2,
  UserCircle2,
  MapPin,
  Milestone,
  Tag,
  ExternalLink,
  BookOpen,
  CheckCircle2,
  CalendarClock,
  AlertCircle,
  UserX,
  ChevronDown,
  ChevronRight,
  X,
  TimerOff,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { KPI } from '@/components/ui/kpi';
import { Progress } from '@/components/ui/progress';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DemoProject,
  PROJECT_STATUS_CONFIG,
  PROJECT_TYPE_CONFIG,
  PROJECT_PRIORITY_CONFIG,
} from '@/data/mockProjectsData';
import { Booking, Resource } from '@/types';
import { Demand } from '@/types/demand';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useDataMode } from '@/contexts/DataModeContext';
import { getBookingColor } from '@/hooks/use-booking-color';
import { projectsApi, demandsApi, EBProjectsCostCentre, EBProjectsBudgetComposite, EBProjectsTimesheetEntry, EBProjectsProject, EBProjectsBudgetConfiguration } from '@/services/api';
import { ResourceBudgetTab } from '@/components/projects/ResourceBudgetTab';
import {
  classifyProjectBookings,
  normaliseBookingStatus,
  openDemandEntries,
  OpenDemandEntry,
} from '@/lib/projectBookingBuckets';

interface ProjectDetailPageProps {
  project: DemoProject | null;
  open: boolean;
  onClose: () => void;
  allBookings?: Booking[];
  bookingsPreFiltered?: boolean;
  resources?: Resource[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  const s = String(name ?? '');
  return s
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

const AVATAR_COLOURS = [
  'bg-violet-500', 'bg-blue-500', 'bg-teal-500', 'bg-emerald-500',
  'bg-amber-500', 'bg-orange-500', 'bg-rose-500', 'bg-cyan-500',
  'bg-indigo-500', 'bg-fuchsia-500',
];
function avatarColour(name: string) {
  const s = String(name ?? '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLOURS[Math.abs(h) % AVATAR_COLOURS.length];
}

// ─── Shared small components ──────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-3">
      {children}
    </p>
  );
}

function InfoRow({ icon: Icon, children }: { icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2.5 text-muted-foreground">
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="text-xs leading-snug">{children}</span>
    </div>
  );
}

function UtilBar({ label, current, max, pct }: {
  label: string; current: string; max: string; pct: number;
}) {
  const barClass =
    pct >= 100 ? '[&>div]:bg-rose-500' :
    pct >= 90  ? '[&>div]:bg-rose-400' :
    pct >= 75  ? '[&>div]:bg-amber-500' : '';
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {current}<span className="mx-1 opacity-40">/</span>{max}
          <span className={`ml-1.5 font-semibold ${pct >= 90 ? 'text-rose-600' : pct >= 75 ? 'text-amber-600' : 'text-foreground'}`}>
            {pct}%
          </span>
        </span>
      </div>
      <Progress value={Math.min(pct, 100)} className={`h-1.5 rounded-full ${barClass}`} />
    </div>
  );
}

// ─── Booking helpers ──────────────────────────────────────────────────────────

function formatBookingDate(startTime: string, endTime: string, durationType: string): string {
  try {
    const start = parseISO(startTime);
    const end = parseISO(endTime);
    const sameDay = format(start, 'yyyy-MM-dd') === format(end, 'yyyy-MM-dd');
    if (durationType === 'full-day') {
      return sameDay
        ? format(start, 'dd MMM yyyy')
        : `${format(start, 'dd MMM')} – ${format(end, 'dd MMM yyyy')}`;
    }
    if (durationType === 'hourly') {
      return `${format(start, 'dd MMM yyyy')} · ${format(start, 'HH:mm')}–${format(end, 'HH:mm')}`;
    }
    if (durationType === 'half-day-am') return `${format(start, 'dd MMM yyyy')} · AM`;
    if (durationType === 'half-day-pm') return `${format(start, 'dd MMM yyyy')} · PM`;
    return format(start, 'dd MMM yyyy');
  } catch {
    return '—';
  }
}

const BOOKING_STATUS_STYLE: Record<string, string> = {
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  planned: 'bg-primary/5 text-primary border-primary/20',
  completed: 'bg-muted text-muted-foreground border-border',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
};

function BookingRow({ booking, resources }: { booking: Booking; resources?: Resource[] }) {
  const { settings } = useScheduler();
  const isUnresourced =
    booking.resourceId === 'unallocated' ||
    booking.resourceId === null ||
    booking.resourceId === undefined;

  // Resolve resource name + role from the resources list
  const resource = !isUnresourced && resources
    ? resources.find(r => String(r.id) === String(booking.resourceId))
    : undefined;
  const resourceName = resource?.name ?? null;
  const resolvedRole = booking.requiredRole || resource?.role || null;

  const dateLabel = formatBookingDate(booking.startTime, booking.endTime, booking.durationType);
  const statusStyle = BOOKING_STATUS_STYLE[normaliseBookingStatus(booking.status)] ?? 'bg-muted text-muted-foreground border-border';
  const resolvedColor = getBookingColor(booking, settings.statusConfigs ?? [], settings.colorOverrides ?? []);

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0">
      <div
        className="mt-0.5 h-9 w-1 rounded-full shrink-0"
        style={{ backgroundColor: resolvedColor || '#94a3b8' }}
      />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold leading-tight truncate">{booking.projectName}</p>
          <span className={`text-[10px] shrink-0 rounded-full border px-2 py-0.5 font-medium capitalize ${statusStyle}`}>
            {booking.status}
          </span>
        </div>

        {isUnresourced ? (
          <div className="flex items-center gap-1 text-[11px] text-amber-600 font-medium">
            <AlertCircle className="h-3 w-3 shrink-0" />
            <span>Unresourced{resolvedRole ? ` · ${resolvedRole}` : ''}</span>
          </div>
        ) : (
          <div className="text-[11px] text-muted-foreground">
            {resolvedRole && resourceName
              ? <><span className="font-medium text-foreground">{resourceName}</span><span className="mx-1 text-muted-foreground/40">·</span><span>{resolvedRole}</span></>
              : resolvedRole
              ? <span>{resolvedRole}</span>
              : resourceName
              ? <span className="font-medium text-foreground">{resourceName}</span>
              : <span>Resource assigned</span>
            }
          </div>
        )}

        <p className="text-[11px] text-muted-foreground tabular-nums">{dateLabel}</p>
      </div>
    </div>
  );
}

/**
 * Hours a booking consumes. Prefers the API-provided duration and falls back to
 * the booking's own dates when it is missing.
 */
function bookingHours(b: Booking): number {
  if (b.durationHours != null && b.durationHours > 0) return b.durationHours;
  try {
    const start = parseISO(b.startTime);
    const end = parseISO(b.endTime);
    if (b.durationType === 'full-day') return Math.max(1, differenceInDays(end, start)) * 8;
    if (b.durationType === 'half-day-am' || b.durationType === 'half-day-pm') return 4;
    return Math.max(0, (end.getTime() - start.getTime()) / (1000 * 60 * 60));
  } catch {
    return 0;
  }
}

function OpenDemandRow({ entry }: { entry: OpenDemandEntry }) {
  const startLabel = entry.requestedStartDate
    ? (() => {
        const parsed = parseISO(entry.requestedStartDate);
        return isValid(parsed) ? format(parsed, 'dd MMM yyyy') : null;
      })()
    : null;

  return (
    <div className="flex items-start gap-3 py-3 border-b border-border/50 last:border-0">
      <div className="mt-0.5 h-9 w-1 rounded-full shrink-0 bg-amber-400" />
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs font-semibold leading-tight truncate">{entry.roleName}</p>
          <span className="text-[10px] shrink-0 rounded-full border px-2 py-0.5 font-medium bg-amber-50 text-amber-700 border-amber-200">
            Resource request
          </span>
        </div>
        <div className="flex items-center gap-1 text-[11px] text-amber-600 font-medium">
          <AlertCircle className="h-3 w-3 shrink-0" />
          <span>{entry.remainingHours}h still to resource</span>
        </div>
        {startLabel && (
          <p className="text-[11px] text-muted-foreground tabular-nums">{startLabel}</p>
        )}
      </div>
    </div>
  );
}

interface BookingSectionProps {
  icon: React.ElementType;
  title: string;
  count: number;
  bookings: Booking[];
  resources?: Resource[];
  emptyText: string;
  defaultOpen: boolean;
  accentClass: string;
  badge?: React.ReactNode;
  demandEntries?: OpenDemandEntry[];
}

function BookingSection({
  icon: Icon,
  title,
  count,
  bookings,
  resources,
  emptyText,
  defaultOpen,
  accentClass,
  badge,
  demandEntries = [],
}: BookingSectionProps) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-2.5">
              <div className="h-6 w-6 rounded-lg bg-muted flex items-center justify-center">
                <Icon className={`h-3.5 w-3.5 ${accentClass}`} />
              </div>
              <span className="text-sm font-semibold">{title}</span>
              {badge}
            </div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium tabular-nums ${accentClass}`}>{count}</span>
              {open
                ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
              }
            </div>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border/50">
            {count === 0 ? (
              <p className="text-xs text-muted-foreground px-4 py-4 text-center">{emptyText}</p>
            ) : (
              <div className="px-4">
                {demandEntries.map((entry) => (
                  <OpenDemandRow key={`demand-${entry.id}`} entry={entry} />
                ))}
                {bookings
                  .slice()
                  .sort((a, b) => String(a.startTime ?? '').localeCompare(String(b.startTime ?? '')))
                  .map((b) => (
                    <BookingRow key={b.id} booking={b} resources={resources} />
                  ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ─── Cost Centre types ───────────────────────────────────────────────────────

type CostCentreStatus = 'Not Started' | 'In Progress' | 'On Hold' | 'Completed' | 'Cancelled';

interface CostCentre {
  code: string;
  name: string;
  type: string;
  status: CostCentreStatus;
  billable: boolean;
  active: boolean;
  costsBudget: number;
  actualCosts: number;
  committedCosts: number;
  totalCosts: number;
  costVariance: number;
  costPct: number;
}

const STATUS_STYLE: Record<CostCentreStatus, string> = {
  'Not Started': 'bg-muted text-muted-foreground border-border',
  'In Progress': 'bg-blue-50 text-blue-700 border-blue-200',
  'On Hold':     'bg-amber-50 text-amber-700 border-amber-200',
  'Completed':   'bg-emerald-50 text-emerald-700 border-emerald-200',
  'Cancelled':   'bg-rose-50 text-rose-700 border-rose-200',
};

// ─── Main component ───────────────────────────────────────────────────────────

export function ProjectDetailPage({
  project,
  open,
  onClose,
  allBookings = [],
  bookingsPreFiltered = false,
  resources = [],
}: ProjectDetailPageProps) {
  // Cost centres — live data from EBProjects API
  const [liveCostCentres, setLiveCostCentres] = useState<EBProjectsCostCentre[]>([]);
  const [costCentresLoading, setCostCentresLoading] = useState(false);
  const [costCentresFetched, setCostCentresFetched] = useState(false);

  // Budget data — live data from EBProjects API
  const [budgetData, setBudgetData] = useState<EBProjectsBudgetComposite | null>(null);
  const [budgetLoading, setBudgetLoading] = useState(false);
  const [budgetFetched, setBudgetFetched] = useState(false);

  // EBProjects project data — for billable status
  const [ebProject, setEbProject] = useState<EBProjectsProject | null>(null);
  const [ebProjectFetched, setEbProjectFetched] = useState(false);

  // Budget configuration — controls which tabs to show
  const [budgetConfig, setBudgetConfig] = useState<EBProjectsBudgetConfiguration | null>(null);

  // Resource requests raised against this project — outstanding hours on them
  // are unresourced work even though no booking exists for them yet.
  const [projectDemands, setProjectDemands] = useState<Demand[]>([]);

  // Timesheet data — live data from EBProjects API (via Access Time)
  const [timesheetData, setTimesheetData] = useState<EBProjectsTimesheetEntry[]>([]);
  const [timesheetLoading, setTimesheetLoading] = useState(false);
  const [timesheetFetched, setTimesheetFetched] = useState(false);

  // Pass the local project ID to the backend; the backend resolves it to the
  // EBProjects ExternalId before calling upstream APIs.  Prefer externalId when
  // available so the backend can skip the DB lookup.
  const projectIdForApi = project?.externalId || project?.id;
  useEffect(() => {
    if (!projectIdForApi) return;
    let cancelled = false;
    setCostCentresLoading(true);
    setCostCentresFetched(false);
    setBudgetLoading(true);
    setBudgetFetched(false);

    projectsApi.getCostCentresFromEBProjects(1, 100, projectIdForApi)
      .then((res) => {
        if (!cancelled) {
          setLiveCostCentres(res.data ?? []);
          setCostCentresFetched(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLiveCostCentres([]);
          setCostCentresFetched(true);
        }
      })
      .finally(() => {
        if (!cancelled) setCostCentresLoading(false);
      });

    projectsApi.getBudgetsFromEBProjects(projectIdForApi)
      .then((res) => {
        if (!cancelled) {
          setBudgetData(res);
          setBudgetFetched(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setBudgetData(null);
          setBudgetFetched(true);
        }
      })
      .finally(() => {
        if (!cancelled) setBudgetLoading(false);
      });

    // Fetch EBProjects project data (for billable status)
    setEbProjectFetched(false);
    projectsApi.getFromEBProjectsById(projectIdForApi)
      .then((res) => {
        if (!cancelled) {
          setEbProject(res);
          setEbProjectFetched(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEbProject(null);
          setEbProjectFetched(true);
        }
      });

    // Fetch timesheets using the local project ID (backend resolves to BillingCode)
    setTimesheetLoading(true);
    setTimesheetFetched(false);
    projectsApi.getTimesheetsFromEBProjects(String(project?.id ?? projectIdForApi))
      .then((res) => {
        if (!cancelled) {
          setTimesheetData(res ?? []);
          setTimesheetFetched(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setTimesheetData([]);
          setTimesheetFetched(true);
        }
      })
      .finally(() => {
        if (!cancelled) setTimesheetLoading(false);
      });

    return () => { cancelled = true; };
  }, [projectIdForApi]);

  // Resource requests for this project — demands are keyed by the source-system
  // project id, which is the same id the EBProjects endpoints are called with.
  useEffect(() => {
    if (!open || !projectIdForApi) {
      setProjectDemands([]);
      return;
    }
    let cancelled = false;
    demandsApi.getByProject(projectIdForApi)
      .then((res) => { if (!cancelled) setProjectDemands(res ?? []); })
      .catch(() => { if (!cancelled) setProjectDemands([]); });
    return () => { cancelled = true; };
  }, [open, projectIdForApi]);

  // Separate effect for org-level budget configuration — re-fetch every time the dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    projectsApi.getBudgetConfigurationFromEBProjects()
      .then((res) => { if (!cancelled) setBudgetConfig(res); })
      .catch(() => { if (!cancelled) setBudgetConfig(null); });
    return () => { cancelled = true; };
  }, [open]);

  const { getScreenMode } = useDataMode();
  const isDemo = getScreenMode('calendar') === 'demo';

  if (!project) return null;
  const statusCfg = PROJECT_STATUS_CONFIG[project.status];
  const typeCfg = PROJECT_TYPE_CONFIG[project.type];
  const priorityCfg = PROJECT_PRIORITY_CONFIG[project.priority];

  // Derive billable status from live EBProjects data (project field, cost centres, or resource budget)
  const isBillable = ebProject?.billable === true
    || liveCostCentres.some((cc) => cc.billable === true)
    || (budgetData?.resourceBudget?.billablePercent != null && budgetData.resourceBudget.billablePercent > 0);
  const billableResolved = ebProjectFetched || costCentresFetched || budgetFetched;

  // Use EBProjects dates (authoritative) when available; fall back to local project dates
  const resolvedStartStr = (ebProject?.startDate && ebProject.startDate.length > 0) ? ebProject.startDate : project.startDate;
  const resolvedEndStr = (ebProject?.endDate && ebProject.endDate.length > 0) ? ebProject.endDate : project.endDate;
  const startDate = parseISO(resolvedStartStr);
  const endDate = parseISO(resolvedEndStr);
  const datesValid = isValid(startDate) && isValid(endDate);
  const totalDays = datesValid ? (differenceInDays(endDate, startDate) || 1) : 1;
  const elapsed = datesValid ? differenceInDays(startOfDay(new Date()), startOfDay(startDate)) : 0;
  const timeProgress = Math.min(100, Math.max(0, Math.round((elapsed / totalDays) * 100)));
  const daysLeft = datesValid ? differenceInDays(startOfDay(endDate), startOfDay(new Date())) : 0;

  // Derive overview utilisation from live budget data when available, falling back to project fields
  const liveTotalBudgetHours = (budgetFetched && budgetData?.roleBudgets?.length)
    ? budgetData.roleBudgets.reduce((s, r) => s + r.budgetHours, 0)
    : 0;
  const liveTotalRemainingHours = (budgetFetched && budgetData?.roleBudgets?.length)
    ? budgetData.roleBudgets.reduce((s, r) => s + r.remainingHours, 0)
    : 0;
  const liveConsumedHours = liveTotalBudgetHours - liveTotalRemainingHours;
  const liveBudgetCost = (budgetFetched && budgetData?.roleBudgets?.length)
    ? budgetData.roleBudgets.reduce((s, r) => s + r.budgetCost, 0)
    : 0;
  const liveActualCost = (budgetFetched && budgetData?.roleBudgets?.length)
    ? budgetData.roleBudgets.reduce((s, r) => s + r.actualCost, 0)
    : 0;

  const daysLabel =
    daysLeft > 0 ? `${daysLeft}d remaining` :
    daysLeft === 0 ? 'Due today' :
    `${Math.abs(daysLeft)}d overdue`;

  // Booking classification
  const projectBookings = bookingsPreFiltered
    ? allBookings.filter((b) => normaliseBookingStatus(b.status) !== 'cancelled')
    : allBookings.filter(
        (b) =>
          b.projectName?.toLowerCase() === project.name?.toLowerCase() &&
          normaliseBookingStatus(b.status) !== 'cancelled',
      );

  // Compute total booked hours from bookings assigned to this project so that
  // "Hours logged" updates immediately when a booking is created.
  // Prefer API-provided durationHours when available; fall back to date calc.
  const bookingTotalHours = projectBookings.reduce((total, b) => total + bookingHours(b), 0);

  // Always include booking hours — use the max of all available sources so
  // that newly created bookings are reflected even when live budget data exists.
  const overviewActualHours = Math.max(
    liveTotalBudgetHours > 0 ? liveConsumedHours : project.actualHours,
    Math.round(bookingTotalHours),
  );
  const overviewBudgetHours = liveTotalBudgetHours > 0 ? liveTotalBudgetHours : project.budgetedHours;
  const hoursProgress = overviewBudgetHours > 0 ? Math.round((overviewActualHours / overviewBudgetHours) * 100) : 0;

  // Derive project progress from live EBProjects data, falling back to hours-based or static
  const projectProgress = (ebProjectFetched && ebProject?.completionPercent != null && ebProject.completionPercent > 0)
    ? Math.round(ebProject.completionPercent)
    : project.percentComplete > 0
      ? project.percentComplete
      : hoursProgress;

  const totalBudgetCost = liveBudgetCost > 0 ? liveBudgetCost : project.budgetItems.reduce((s, i) => s + i.budgetedHours * i.costRate, 0);
  const totalActualCost = liveBudgetCost > 0 ? liveActualCost : project.budgetItems.reduce((s, i) => s + i.actualHours * i.costRate, 0);

  const budgetPct = totalBudgetCost > 0 ? Math.round((totalActualCost / totalBudgetCost) * 100) : 0;

  const {
    completed: completedBookings,
    planned: plannedBookings,
    unresourced: unresourcedBookings,
  } = classifyProjectBookings(projectBookings);

  // Outstanding resource requests count as unresourced work even before any
  // booking exists for them.
  const openDemands = openDemandEntries(projectDemands);
  const unresourcedCount = unresourcedBookings.length + openDemands.length;

  // Build team from bookings
  const resourceLookup: Record<string, Resource> = {};
  resources.forEach((r) => { if (r.id) resourceLookup[r.id] = r; });

  // Scheduler hours booked per role, keyed by lower-cased role name. The
  // Resource Budget tab adds these on top of PSA timesheet actuals the same way
  // the suggested resource plan drawer does.
  const bookedHoursByRole = projectBookings.reduce<Record<string, number>>((acc, b) => {
    const hours = bookingHours(b);
    if (hours <= 0) return acc;
    const resIds = b.resourceIds?.length ? b.resourceIds : b.resourceId ? [b.resourceId] : [];
    const roles = resIds
      .filter((rid) => rid && rid !== 'unallocated')
      .map((rid) => resourceLookup[rid]?.role || b.requiredRole || '');
    const targetRoles = roles.length > 0 ? roles : [b.requiredRole ?? ''];
    targetRoles.forEach((role) => {
      if (!role) return;
      const key = role.toLowerCase();
      acc[key] = (acc[key] ?? 0) + hours;
    });
    return acc;
  }, {});

  interface DerivedTeamMember {
    id: string; name: string; role: string; department: string;
    email: string; avatarUrl?: string; allocatedHours: number;
    actualHours: number; remainingHours: number;
    status: 'On Track' | 'At Risk' | 'Over Allocated';
    bookingStatus: 'Confirmed' | 'Planned' | 'No Booking';
    bookingCount: number;
  }

  const derivedTeamMembers: DerivedTeamMember[] = (() => {
    const memberMap: Record<string, {
      id: string; name: string; role: string; department: string;
      email: string; avatarUrl?: string; allocatedHours: number;
      actualHours: number; bookingCount: number;
      hasConfirmed: boolean; hasPlanned: boolean;
    }> = {};

    projectBookings.forEach((b) => {
      const resIds = b.resourceIds?.length ? b.resourceIds : b.resourceId ? [b.resourceId] : [];
      resIds.forEach((rid) => {
        if (!rid || rid === 'unallocated') return;
        if (!memberMap[rid]) {
          const res = resourceLookup[rid];
          memberMap[rid] = {
            id: rid, name: res?.name ?? rid, role: res?.role ?? b.requiredRole ?? '',
            department: res?.department ?? '', email: res?.email ?? '',
            avatarUrl: res?.photoUrl, allocatedHours: 0, actualHours: 0,
            bookingCount: 0, hasConfirmed: false, hasPlanned: false,
          };
        }
        const bEnd = parseISO(b.endTime);
        const hours = bookingHours(b);
        memberMap[rid].allocatedHours += hours;
        memberMap[rid].bookingCount += 1;
        const status = normaliseBookingStatus(b.status);
        if (status === 'confirmed' || status === 'completed') memberMap[rid].hasConfirmed = true;
        else if (status === 'planned') memberMap[rid].hasPlanned = true;
        if (status === 'completed' || (isPast(bEnd) && !isToday(bEnd))) {
          memberMap[rid].actualHours += hours;
        }
      });
    });

    if (project.projectManagerId && !memberMap[project.projectManagerId]) {
      const pmRes = resourceLookup[project.projectManagerId];
      if (pmRes) {
        memberMap[project.projectManagerId] = {
          id: project.projectManagerId, name: pmRes.name ?? project.projectManagerName,
          role: pmRes.role ?? 'Project Manager', department: pmRes.department ?? '',
          email: pmRes.email ?? '', avatarUrl: pmRes.photoUrl,
          allocatedHours: 0, actualHours: 0, bookingCount: 0,
          hasConfirmed: false, hasPlanned: false,
        };
      }
    }

    return Object.values(memberMap)
      .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')))
      .map((m) => {
        const remaining = Math.max(0, m.allocatedHours - m.actualHours);
        const utilPct = m.allocatedHours > 0 ? (m.actualHours / m.allocatedHours) * 100 : 0;
        const status: DerivedTeamMember['status'] =
          utilPct > 100 ? 'Over Allocated' : utilPct > 80 ? 'At Risk' : 'On Track';
        const bookingStatus: DerivedTeamMember['bookingStatus'] =
          m.hasConfirmed ? 'Confirmed' : m.hasPlanned ? 'Planned' : 'No Booking';
        return {
          id: m.id, name: m.name, role: m.role, department: m.department,
          email: m.email, avatarUrl: m.avatarUrl,
          allocatedHours: Math.round(m.allocatedHours),
          actualHours: Math.round(m.actualHours),
          remainingHours: Math.round(remaining),
          status, bookingStatus, bookingCount: m.bookingCount,
        };
      });
  })();

  const hasEBBudgetBreakdown = project.budgetItems.some(
    (i) => i.id.endsWith('-labour') || i.id.endsWith('-expense') || i.id.endsWith('-revenue'),
  );
  const hasLiveBudget = budgetFetched && budgetData !== null &&
    (budgetData.costBudget != null || budgetData.salesBudget != null || budgetData.resourceBudget != null);

  // Show Resource Budget tab when:
  //   1. In demo mode (no live integration available), OR
  //   2. The project has local budgetItems data (e.g. from roleBudgetHours mapping), OR
  //   3. The EBProjects budget configuration confirms it is enabled
  const hasLocalBudgetData = project.budgetItems.length > 0;
  const showResourceBudgetTab =
    isDemo
    || hasLocalBudgetData
    || (budgetConfig != null
      && budgetConfig.budgetMode !== 'disabled'
      && budgetConfig.resourceBudgetEnabled);

  const mapCCStatus = (s?: string): CostCentreStatus => {
    if (!s) return 'In Progress';
    const lower = s.toLowerCase();
    if (lower.includes('not started')) return 'Not Started';
    if (lower.includes('progress') || lower.includes('active')) return 'In Progress';
    if (lower.includes('hold')) return 'On Hold';
    if (lower.includes('complet') || lower.includes('closed')) return 'Completed';
    if (lower.includes('cancel')) return 'Cancelled';
    return 'In Progress';
  };

  const costCentres: CostCentre[] = costCentresFetched && liveCostCentres.length > 0
    ? liveCostCentres.map((cc) => {
        const costsBudget = cc.costsBudget ?? 0;
        const actualCosts = cc.actualCosts ?? 0;
        const committedCosts = cc.committedCosts ?? 0;
        const totalCosts = actualCosts + committedCosts;
        const costVariance = costsBudget - actualCosts - committedCosts;
        const costPct = costsBudget > 0 ? Math.round((actualCosts / costsBudget) * 100) : 0;
        return {
          code: cc.code ?? '',
          name: cc.name ?? '',
          type: cc.type || cc.description || 'General',
          status: mapCCStatus(cc.status),
          billable: cc.billable ?? true,
          active: cc.isActive ?? (cc.status ? !cc.status.toLowerCase().includes('cancel') : true),
          costsBudget,
          actualCosts,
          committedCosts,
          totalCosts,
          costVariance,
          costPct,
        };
      })
    : [];

  const ccTotals = costCentres.reduce(
    (acc, cc) => ({
      costsBudget: acc.costsBudget + cc.costsBudget,
      actualCosts: acc.actualCosts + cc.actualCosts,
      committedCosts: acc.committedCosts + cc.committedCosts,
      totalCosts: acc.totalCosts + cc.totalCosts,
      costVariance: acc.costVariance + cc.costVariance,
    }),
    { costsBudget: 0, actualCosts: 0, committedCosts: 0, totalCosts: 0, costVariance: 0 },
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogPrimitive.Portal>
        {/* Backdrop */}
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        {/* Modal panel — large centred card */}
        <DialogPrimitive.Content
          className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 w-[92vw] max-w-5xl h-[90vh] bg-background rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
        >
          {/* Visually hidden title for a11y */}
          <DialogPrimitive.Title className="sr-only">{project.name} — Project Details</DialogPrimitive.Title>

      {/* ── Modal Header ─────────────────────────────────────────────────── */}
      <div className="shrink-0 border-b bg-background px-6 py-4">
        {/* Title row */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
              <FolderKanban className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 mb-0.5">
                <span className="text-xs font-mono text-muted-foreground">{project.code}</span>
              </div>
              <h1 className="text-xl font-semibold leading-tight truncate">{project.name}</h1>
              {project.description && (
                <p className="text-sm text-muted-foreground mt-0.5 line-clamp-1">{project.description}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {/* Priority pill */}
            <span className="text-[11px] font-medium rounded-full px-2.5 py-0.5 border border-border text-muted-foreground bg-muted/40">
              {priorityCfg.label}
            </span>
            {/* Type pill */}
            <span className="text-[11px] font-medium rounded-full px-2.5 py-0.5 border border-border text-muted-foreground bg-muted/40">
              {typeCfg.label}
            </span>
            {/* Billable indicator — live from EBProjects */}
            {billableResolved && (
              <span className={`text-[11px] font-medium rounded-full px-2.5 py-0.5 border inline-flex items-center gap-1 ${isBillable ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950 dark:text-emerald-300 dark:border-emerald-800' : 'bg-muted/40 text-muted-foreground border-border'}`}>
                <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${isBillable ? 'bg-emerald-500' : 'bg-muted-foreground/40'}`} />
                {isBillable ? 'Billable' : 'Non-Billable'}
              </span>
            )}
            {/* Status badge */}
            <Badge
              variant="outline"
              accent={statusCfg.accent as any}
              className="text-xs px-2.5 py-0.5 rounded-full"
            >
              <span className="flex items-center gap-1.5">
                <span className={`h-1.5 w-1.5 rounded-full ${statusCfg.dotClass} shrink-0`} />
                {statusCfg.label}
              </span>
            </Badge>
            {project.projectUrl && (
              <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs" asChild>
                <a href={project.projectUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Open in Projects
                </a>
              </Button>
            )}
            {/* Close button */}
            <button
              onClick={onClose}
              className="ml-1 h-8 w-8 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Meta row */}
        <div className="flex items-center gap-6 mt-3 text-xs text-muted-foreground flex-wrap">
          {project.customerName && (
            <InfoRow icon={Building2}>{project.customerName}</InfoRow>
          )}
          <InfoRow icon={UserCircle2}>{project.projectManagerName}</InfoRow>
          {project.region && <InfoRow icon={MapPin}>{project.region}</InfoRow>}
          {datesValid && (
          <InfoRow icon={Calendar}>
            <span className={
              daysLeft < 0 ? 'text-rose-500 font-medium' :
              daysLeft <= 7 ? 'text-amber-500 font-medium' :
              'text-muted-foreground'
            }>
              {format(endDate, 'dd MMM yyyy')}
              <span className="opacity-60 ml-1.5 font-normal">({daysLabel})</span>
            </span>
          </InfoRow>
          )}
          <div className="flex items-center gap-2 min-w-[160px]">
            <Progress value={projectProgress} className="h-1.5 w-24" />
            <span className="font-semibold text-foreground tabular-nums">{projectProgress}%</span>
          </div>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <Tabs defaultValue="overview" className="flex flex-col flex-1 min-h-0 overflow-hidden">

        <div className="shrink-0 border-b bg-background px-6">
          <TabsList variant="default" className="h-10 gap-1 bg-transparent p-0 border-0 rounded-none">
            <TabsTrigger value="overview"    variant="default" className="h-10 rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none px-3 text-sm">Overview</TabsTrigger>
            <TabsTrigger value="team"        variant="default" className="h-10 rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none px-3 text-sm">Team</TabsTrigger>
            <TabsTrigger value="bookings"    variant="default" className="h-10 rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none px-3 text-sm"
              badge={unresourcedCount > 0 ? unresourcedCount : undefined}
              badgeVariant="default"
            >Bookings</TabsTrigger>
            {showResourceBudgetTab && (
            <TabsTrigger value="resource-budget"  variant="default" className="h-10 rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none px-3 text-sm">Resource Budget</TabsTrigger>
            )}
            <TabsTrigger value="cost-centres"     variant="default" className="h-10 rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none px-3 text-sm">Cost Centres</TabsTrigger>
            <TabsTrigger value="time"             variant="default" className="h-10 rounded-none border-0 border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:shadow-none px-3 text-sm">Time</TabsTrigger>
          </TabsList>
        </div>

        {/* ── OVERVIEW ──────────────────────────────────────────────────── */}
        <TabsContent value="overview" className="flex-1 overflow-y-auto min-h-0 p-6">
          {/* Show loading skeleton until key data resolves */}
          {(budgetLoading || costCentresLoading || !ebProjectFetched) && !budgetFetched ? (
            <div className="max-w-4xl mx-auto space-y-6 animate-pulse">
              <div className="space-y-3">
                <div className="h-3 w-24 rounded bg-muted" />
                <div className="h-4 w-3/4 rounded bg-muted" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="rounded-xl border border-border bg-card p-4 space-y-2">
                    <div className="h-2 w-16 rounded bg-muted" />
                    <div className="h-5 w-12 rounded bg-muted" />
                    <div className="h-2 w-20 rounded bg-muted" />
                  </div>
                ))}
              </div>
              <div className="space-y-3">
                <div className="h-3 w-32 rounded bg-muted" />
                <div className="h-8 w-full rounded bg-muted" />
                <div className="h-8 w-full rounded bg-muted" />
                <div className="h-8 w-full rounded bg-muted" />
              </div>
            </div>
          ) : (
          <div className="max-w-4xl mx-auto space-y-6">
            {project.description && (
              <div>
                <SectionLabel>About</SectionLabel>
                <p className="text-sm text-muted-foreground leading-relaxed">{project.description}</p>
              </div>
            )}

            <div>
              <SectionLabel>At a Glance</SectionLabel>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Start Date', value: datesValid ? format(startDate, 'dd MMM yyyy') : '—', sub: datesValid ? (isPast(startDate) ? 'Started' : 'Upcoming') : 'Not set', icon: Calendar },
                  { label: 'End Date', value: datesValid ? format(endDate, 'dd MMM yyyy') : '—', sub: datesValid ? (daysLeft < 0 ? `${Math.abs(daysLeft)}d overdue` : daysLeft === 0 ? 'Ends today' : `${daysLeft}d remaining`) : 'Not set', icon: CalendarClock },
                  { label: 'Budgeted Hours', value: `${overviewBudgetHours.toLocaleString()}h`, sub: `${overviewActualHours.toLocaleString()}h logged`, icon: Clock },
                  { label: 'Time Elapsed',   value: `${timeProgress}%`, sub: `${Math.max(0, elapsed)}d elapsed`, icon: TrendingUp },
                  { label: 'Team Size',      value: String(derivedTeamMembers.length), sub: `${new Set(derivedTeamMembers.map((t) => t.department).filter(Boolean)).size || 0} dept(s)`, icon: Users },
                  { label: project.milestones.length > 0 ? 'Milestones' : 'Bookings',
                    value: project.milestones.length > 0 ? String(project.milestones.length) : String(projectBookings.length),
                    sub: project.milestones.length > 0 ? `${project.milestones.filter((m) => m.status === 'completed').length} completed` : `${unresourcedBookings.length} unresourced`,
                    icon: project.milestones.length > 0 ? Milestone : BookOpen },
                ].map(({ label, value, sub, icon: Icon }) => (
                  <div key={label} className="rounded-xl border border-border bg-card p-4">
                    <div className="flex items-center justify-between mb-2.5">
                      <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</span>
                      <Icon className="h-3.5 w-3.5 text-muted-foreground/30" />
                    </div>
                    <p className="text-xl font-semibold tracking-tight">{value}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <SectionLabel>Utilisation</SectionLabel>
              <div className="rounded-xl border border-border bg-card p-4 space-y-4">
                <UtilBar label="Hours logged" current={`${overviewActualHours.toLocaleString()}h`} max={`${overviewBudgetHours.toLocaleString()}h`} pct={hoursProgress} />
                <UtilBar label="Budget cost" current={`£${Math.round(totalActualCost).toLocaleString()}`} max={`£${Math.round(totalBudgetCost).toLocaleString()}`} pct={budgetPct} />
              </div>
            </div>

            {project.tags.length > 0 && (
              <div>
                <SectionLabel>Tags</SectionLabel>
                <div className="flex flex-wrap gap-1.5">
                  {project.tags.map((tag) => (
                    <div key={tag} className="flex items-center gap-1 text-xs text-muted-foreground bg-muted rounded-full px-2.5 py-1">
                      <Tag className="h-3 w-3 opacity-50" />
                      {tag}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          )}
        </TabsContent>

        {/* ── TEAM ──────────────────────────────────────────────────────── */}
        <TabsContent value="team" className="flex-1 overflow-y-auto min-h-0 p-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {derivedTeamMembers.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Users className="h-5 w-5 text-muted-foreground/40" />
                </div>
                <p className="text-sm text-muted-foreground">No team members assigned yet.</p>
                <p className="text-[11px] text-muted-foreground/70">Resources booked against this project will appear here.</p>
              </div>
            ) : (
              <>
                <div>
                  <p className="text-sm font-semibold">Team Management</p>
                  <p className="text-xs text-muted-foreground">Comprehensive team member tracking and allocation</p>
                </div>
                <div className="rounded-xl border border-border overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="hover:bg-transparent border-b border-border bg-muted/30">
                        <TableHead className="text-[10px] uppercase tracking-wider pl-4 text-muted-foreground font-semibold h-8">Name</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold h-8">Role</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Allocated Hrs</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Actual Hrs</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Remaining</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold h-8">Status</TableHead>
                        <TableHead className="text-[10px] uppercase tracking-wider pr-4 text-muted-foreground font-semibold h-8">Booking Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {derivedTeamMembers.map((member) => {
                        const colour = avatarColour(member.name);
                        const statusClass = member.status === 'On Track' ? 'text-emerald-600' : member.status === 'At Risk' ? 'text-amber-600' : 'text-rose-600';
                        const bookingStatusClass = member.bookingStatus === 'Confirmed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : member.bookingStatus === 'Planned' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-amber-50 text-amber-700 border-amber-200';
                        const bookingIcon = member.bookingStatus === 'Confirmed' ? <CheckCircle2 className="h-3 w-3 mr-1 inline" /> : member.bookingStatus === 'No Booking' ? <AlertCircle className="h-3 w-3 mr-1 inline" /> : null;
                        return (
                          <TableRow key={member.id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                            <TableCell className="py-2.5 pl-4">
                              <div className="flex items-center gap-2.5">
                                <Avatar className="h-7 w-7 shrink-0">
                                  <AvatarImage src={member.avatarUrl} alt={member.name} className="object-cover" />
                                  <AvatarFallback className={`${colour} text-white font-semibold text-[10px]`}>{getInitials(member.name)}</AvatarFallback>
                                </Avatar>
                                <span className="text-xs font-medium truncate">{member.name}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground py-2.5 whitespace-nowrap">{member.role}</TableCell>
                            <TableCell className="text-xs py-2.5 text-right tabular-nums font-medium">
                              {member.allocatedHours > 0 ? `${member.allocatedHours}h` : <span className="text-muted-foreground/60 italic">Not allocated</span>}
                            </TableCell>
                            <TableCell className="text-xs py-2.5 text-right tabular-nums">{member.actualHours}h</TableCell>
                            <TableCell className="text-xs py-2.5 text-right tabular-nums">{member.remainingHours}h</TableCell>
                            <TableCell className="py-2.5"><span className={`text-xs font-semibold ${statusClass}`}>{member.status}</span></TableCell>
                            <TableCell className="py-2.5 pr-4">
                              <span className={`text-[10px] rounded-full border px-2 py-0.5 font-medium inline-flex items-center ${bookingStatusClass}`}>
                                {bookingIcon}{member.bookingStatus}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                <div className="text-[11px] text-muted-foreground text-center">
                  {derivedTeamMembers.length} member{derivedTeamMembers.length !== 1 ? 's' : ''}
                  {' · '}{derivedTeamMembers.filter((m) => m.bookingStatus === 'Confirmed').length} confirmed
                  {' · '}{derivedTeamMembers.filter((m) => m.bookingStatus === 'No Booking').length} without bookings
                </div>
              </>
            )}
          </div>
        </TabsContent>

        {/* ── BOOKINGS ──────────────────────────────────────────────────── */}
        <TabsContent value="bookings" className="flex-1 overflow-y-auto min-h-0 p-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {projectBookings.length === 0 && openDemands.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <BookOpen className="h-5 w-5 text-muted-foreground/40" />
                </div>
                <p className="text-sm font-medium">No bookings found</p>
                <p className="text-xs text-muted-foreground mt-0.5">Bookings raised against this project will appear here.</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { label: 'Completed',   value: completedBookings.length,   colour: 'text-emerald-600', bg: 'bg-emerald-50 border-emerald-100' },
                    { label: 'Planned',     value: plannedBookings.length,     colour: 'text-primary',     bg: 'bg-primary/5 border-primary/10' },
                    { label: 'Unresourced', value: unresourcedCount, colour: unresourcedCount > 0 ? 'text-amber-600' : 'text-muted-foreground', bg: unresourcedCount > 0 ? 'bg-amber-50 border-amber-100' : 'bg-muted/30 border-border' },
                  ].map(({ label, value, colour, bg }) => (
                    <div key={label} className={`rounded-xl border ${bg} p-3 text-center`}>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
                      <p className={`text-xl font-semibold tabular-nums ${colour}`}>{value}</p>
                    </div>
                  ))}
                </div>
                <BookingSection icon={CalendarClock} title="Planned" count={plannedBookings.length} bookings={plannedBookings} resources={resources} emptyText="No planned bookings." defaultOpen={true} accentClass="text-primary" />
                <BookingSection icon={UserX} title="Unresourced" count={unresourcedCount} bookings={unresourcedBookings} demandEntries={openDemands} resources={resources} emptyText="No unresourced bookings — all demand is covered." defaultOpen={unresourcedCount > 0} accentClass={unresourcedCount > 0 ? 'text-amber-600' : 'text-muted-foreground'}
                  badge={unresourcedCount > 0 ? (
                    <span className="text-[10px] font-medium rounded-full px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200">Needs resourcing</span>
                  ) : undefined}
                />
                <BookingSection icon={CheckCircle2} title="Completed" count={completedBookings.length} bookings={completedBookings} resources={resources} emptyText="No completed bookings yet." defaultOpen={false} accentClass="text-emerald-600" />
              </>
            )}
          </div>
        </TabsContent>

        {/* ── RESOURCE BUDGET ───────────────────────────────────────────── */}
        {showResourceBudgetTab && (
        <TabsContent value="resource-budget" className="flex-1 overflow-y-auto min-h-0">

          <ResourceBudgetTab
            project={project}
            budgetData={budgetData}
            budgetLoading={budgetLoading}
            budgetFetched={budgetFetched}
            hasLiveBudget={hasLiveBudget}
            bookedHoursByRole={bookedHoursByRole}
          />
        </TabsContent>
        )}




        {/* ── COST CENTRES ──────────────────────────────────────────────── */}
        <TabsContent value="cost-centres" className="flex-1 overflow-y-auto min-h-0 p-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {/* Header + action */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">Cost Centres</p>
                <p className="text-xs text-muted-foreground">Cost centre breakdown for {project.name}</p>
              </div>

            </div>

            {costCentresLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                <p className="text-xs text-muted-foreground">Loading cost centres…</p>
              </div>
            ) : costCentres.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Building2 className="h-5 w-5 text-muted-foreground/40" />
                </div>
                <p className="text-sm font-medium">No cost centres found</p>
                <p className="text-xs text-muted-foreground mt-0.5">Cost centres are managed in the Projects application.</p>
              </div>
            ) : (() => {
              const hasFinancials = costCentres.some((cc) => cc.costsBudget > 0 || cc.actualCosts > 0 || cc.totalCosts > 0);
              return (
                <>
                  {/* Table */}
                  <div className="rounded-xl border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent bg-muted/30">
                          <TableHead className="text-xs font-semibold pl-4 h-9">Code</TableHead>
                          <TableHead className="text-xs font-semibold h-9">Name</TableHead>
                          <TableHead className="text-xs font-semibold h-9">Type</TableHead>
                          <TableHead className="text-xs font-semibold h-9">Status</TableHead>
                          <TableHead className="text-xs font-semibold text-center h-9">Billable</TableHead>
                          <TableHead className="text-xs font-semibold text-center h-9">Active</TableHead>
                          {hasFinancials && (
                            <>
                              <TableHead className="text-xs font-semibold text-right h-9">Costs Budget</TableHead>
                              <TableHead className="text-xs font-semibold text-right h-9">Actual Costs</TableHead>
                              <TableHead className="text-xs font-semibold text-right h-9">Committed</TableHead>
                              <TableHead className="text-xs font-semibold text-right h-9">Total Costs</TableHead>
                              <TableHead className="text-xs font-semibold text-right h-9">Variance</TableHead>
                              <TableHead className="text-xs font-semibold text-right pr-4 h-9">Cost %</TableHead>
                            </>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costCentres.map((cc) => (
                          <TableRow key={cc.code} className="hover:bg-muted/30 cursor-pointer transition-colors">
                            <TableCell className="text-xs text-muted-foreground font-mono py-3 pl-4">{cc.code}</TableCell>
                            <TableCell className="text-xs font-medium py-3">{cc.name}</TableCell>
                            <TableCell className="text-xs text-muted-foreground py-3">{cc.type}</TableCell>
                            <TableCell className="py-3">
                              <span className={`text-[11px] rounded-full border px-2.5 py-0.5 font-medium whitespace-nowrap ${STATUS_STYLE[cc.status]}`}>
                                {cc.status}
                              </span>
                            </TableCell>
                            <TableCell className="py-3 text-center">
                              <span className={`text-[11px] rounded-full px-2.5 py-0.5 font-medium ${cc.billable ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground border border-border'}`}>
                                {cc.billable ? 'Yes' : 'No'}
                              </span>
                            </TableCell>
                            <TableCell className="py-3 text-center">
                              <span className={`text-[11px] rounded-full px-2.5 py-0.5 font-medium ${cc.active ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground border border-border'}`}>
                                {cc.active ? 'Yes' : 'No'}
                              </span>
                            </TableCell>
                            {hasFinancials && (
                              <>
                                <TableCell className="text-xs text-right tabular-nums py-3">£{cc.costsBudget.toFixed(2)}</TableCell>
                                <TableCell className="text-xs text-right tabular-nums py-3">£{cc.actualCosts.toFixed(2)}</TableCell>
                                <TableCell className="text-xs text-right tabular-nums py-3">£{cc.committedCosts.toFixed(2)}</TableCell>
                                <TableCell className="text-xs text-right tabular-nums py-3">£{cc.totalCosts.toFixed(2)}</TableCell>
                                <TableCell className={`text-xs text-right tabular-nums py-3 font-medium ${cc.costVariance < 0 ? 'text-rose-600' : 'text-foreground'}`}>
                                  £{cc.costVariance.toFixed(2)}
                                </TableCell>
                                <TableCell className="text-xs text-right tabular-nums py-3 pr-4">{cc.costPct.toFixed(1)}%</TableCell>
                              </>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                      {hasFinancials && (
                        <tfoot>
                          <tr className="border-t-2 border-border bg-muted/20 font-semibold">
                            <td className="text-xs py-3 pl-4">Total</td>
                            <td className="text-xs py-3">—</td>
                            <td className="text-xs py-3">—</td>
                            <td className="text-xs py-3">—</td>
                            <td className="text-xs py-3">—</td>
                            <td className="text-xs py-3">—</td>
                            <td className="text-xs text-right tabular-nums py-3">£{ccTotals.costsBudget.toFixed(2)}</td>
                            <td className="text-xs text-right tabular-nums py-3">£{ccTotals.actualCosts.toFixed(2)}</td>
                            <td className="text-xs text-right tabular-nums py-3">£{ccTotals.committedCosts.toFixed(2)}</td>
                            <td className="text-xs text-right tabular-nums py-3">£{ccTotals.totalCosts.toFixed(2)}</td>
                            <td className={`text-xs text-right tabular-nums py-3 ${ccTotals.costVariance < 0 ? 'text-rose-600' : ''}`}>£{ccTotals.costVariance.toFixed(2)}</td>
                            <td className="text-xs text-right tabular-nums py-3 pr-4">0.0%</td>
                          </tr>
                        </tfoot>
                      )}
                    </Table>
                  </div>
                </>
              );
            })()}
          </div>
        </TabsContent>

        {/* ── TIME ──────────────────────────────────────────────────────── */}
        <TabsContent value="time" className="flex-1 overflow-y-auto min-h-0 p-6">
          <div className="max-w-4xl mx-auto space-y-5">
            {timesheetLoading ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <p className="text-sm text-muted-foreground">Loading timesheet data...</p>
              </div>
            ) : timesheetFetched && timesheetData.length > 0 ? (() => {
              const totalHours = timesheetData.reduce((s, e) => s + e.totalHours, 0);
              const billableHours = timesheetData.filter((e) => e.billable).reduce((s, e) => s + e.totalHours, 0);
              const billablePct = totalHours > 0 ? Math.round((billableHours / totalHours) * 100) : 0;
              const approvedHours = timesheetData.filter((e) => String(e.status ?? '').toLowerCase() === 'approved').reduce((s, e) => s + e.totalHours, 0);
              const approvedPct = totalHours > 0 ? Math.round((approvedHours / totalHours) * 100) : 0;
              const pendingHours = totalHours - approvedHours;
              const pendingPct = totalHours > 0 ? Math.round((pendingHours / totalHours) * 100) : 0;

              return (
                <>
                  <div>
                    <SectionLabel>Time Summary</SectionLabel>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[
                        { label: 'Total Hours', value: `${totalHours.toFixed(1)}h`, sub: `${billableHours.toFixed(1)}h billable / ${(totalHours - billableHours).toFixed(1)}h non-billable` },
                        { label: 'Billable Hours', value: `${billableHours.toFixed(1)}h`, sub: `${billablePct}% of total` },
                        { label: 'Approved Hours', value: `${approvedHours.toFixed(1)}h`, sub: `${approvedPct}% of total` },
                        { label: 'Pending Hours', value: `${pendingHours.toFixed(1)}h`, sub: `${pendingPct}% not yet approved` },
                      ].map(({ label, value, sub }) => (
                        <div key={label} className="rounded-xl border border-border bg-card p-4">
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">{label}</p>
                          <p className="text-xl font-semibold tracking-tight tabular-nums">{value}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              );
            })() : project.timeEntries.length === 0 && project.actualHours === 0 && project.budgetedHours === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-center">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <Clock className="h-5 w-5 text-muted-foreground/40" />
                </div>
                <p className="text-sm font-medium">No time data available</p>
                <p className="text-xs text-muted-foreground mt-0.5">Time logged against this project will appear here.</p>
              </div>
            ) : project.timeEntries.length > 0 ? (() => {
              const totalHours = project.timeEntries.reduce((s, e) => s + e.hours, 0);
              const billableHours = project.timeEntries.filter((e) => e.billable).reduce((s, e) => s + e.hours, 0);
              const nonBillableHours = totalHours - billableHours;
              const approvedHours = project.timeEntries.filter((e) => e.status === 'approved').reduce((s, e) => s + e.hours, 0);
              const pendingHours = totalHours - approvedHours;
              return (
              <>
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-center divide-x">
                      <div className="flex-1 px-6 first:pl-4">
                        <KPI
                          variant="mini"
                          label="Total Hours"
                          value={totalHours.toFixed(1)}
                          description={`${billableHours.toFixed(1)} billable, ${nonBillableHours.toFixed(1)} non-billable`}
                          icon={<Clock className="w-4 h-4" />}
                          accent="info"
                        />
                      </div>
                      <div className="flex-1 px-6">
                        <KPI
                          variant="mini"
                          label="Billable Hours"
                          value={billableHours.toFixed(1)}
                          description={`${totalHours > 0 ? ((billableHours / totalHours) * 100).toFixed(0) : 0}% of total hours`}
                          icon={<TrendingUp className="w-4 h-4" />}
                          accent="success"
                        />
                      </div>
                      <div className="flex-1 px-6">
                        <KPI
                          variant="mini"
                          label="Approved Hours"
                          value={approvedHours.toFixed(1)}
                          description={`${totalHours > 0 ? ((approvedHours / totalHours) * 100).toFixed(0) : 0}% of total hours`}
                          icon={<CheckCircle2 className="w-4 h-4" />}
                          accent="success"
                        />
                      </div>
                      <div className="flex-1 px-6 last:pr-4">
                        <KPI
                          variant="mini"
                          label="Pending Hours"
                          value={pendingHours.toFixed(1)}
                          description={`${totalHours > 0 ? ((pendingHours / totalHours) * 100).toFixed(0) : 0}% not yet approved`}
                          icon={<TimerOff className="w-4 h-4" />}
                          accent="warning"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <div>
                  <SectionLabel>Entries</SectionLabel>
                  <div className="rounded-xl border border-border overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="hover:bg-transparent border-b border-border bg-muted/30">
                          <TableHead className="text-[10px] uppercase tracking-wider pl-4 text-muted-foreground font-semibold h-8">Date</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold h-8">Resource</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold h-8">Note</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider text-right text-muted-foreground font-semibold h-8">Hrs</TableHead>
                          <TableHead className="text-[10px] uppercase tracking-wider pr-4 text-muted-foreground font-semibold h-8">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {project.timeEntries.map((entry) => {
                          const approved = entry.status === 'approved';
                          const rejected = entry.status === 'rejected';
                          return (
                            <TableRow key={entry.id} className="border-b border-border/50 last:border-0 hover:bg-muted/20">
                              <TableCell className="text-[11px] py-2.5 pl-4 whitespace-nowrap font-mono text-muted-foreground">{format(parseISO(entry.date), 'dd MMM')}</TableCell>
                              <TableCell className="text-xs py-2.5 font-medium">{entry.resource}</TableCell>
                              <TableCell className="text-[11px] py-2.5 max-w-[120px] truncate text-muted-foreground">{entry.description}</TableCell>
                              <TableCell className="text-xs py-2.5 text-right font-semibold tabular-nums">{entry.hours}h</TableCell>
                              <TableCell className="py-2.5 pr-4">
                                <span className={`text-[10px] rounded-full border px-2 py-0.5 font-medium inline-block ${approved ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : rejected ? 'bg-rose-50 text-rose-700 border-rose-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                                  {entry.status.charAt(0).toUpperCase() + entry.status.slice(1)}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </>
              );
            })() : (
              <>
                <Card>
                  <CardContent className="py-4">
                    <div className="flex items-center divide-x">
                      <div className="flex-1 px-6 first:pl-4">
                        <KPI
                          variant="mini"
                          label="Total Hours"
                          value={`${project.actualHours.toLocaleString()}h`}
                          description={`of ${project.budgetedHours.toLocaleString()}h budgeted`}
                          icon={<Clock className="w-4 h-4" />}
                          accent="info"
                        />
                      </div>
                      <div className="flex-1 px-6">
                        <KPI
                          variant="mini"
                          label="Billable Hours"
                          value="—"
                          description="No time entries available"
                          icon={<TrendingUp className="w-4 h-4" />}
                          accent="success"
                        />
                      </div>
                      <div className="flex-1 px-6">
                        <KPI
                          variant="mini"
                          label="Approved Hours"
                          value="—"
                          description="No time entries available"
                          icon={<CheckCircle2 className="w-4 h-4" />}
                          accent="success"
                        />
                      </div>
                      <div className="flex-1 px-6 last:pr-4">
                        <KPI
                          variant="mini"
                          label="Pending Hours"
                          value="—"
                          description="No time entries available"
                          icon={<TimerOff className="w-4 h-4" />}
                          accent="warning"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {datesValid && (
                <div className="rounded-xl border border-border bg-card p-4">
                  <UtilBar label="Hours utilisation" current={`${project.actualHours.toLocaleString()}h`} max={`${project.budgetedHours.toLocaleString()}h`} pct={hoursProgress} />
                </div>
                )}
                {projectProgress > 0 && (
                  <div className="rounded-xl border border-border bg-card p-4">
                    <UtilBar label="Project completion" current={`${projectProgress}%`} max="100%" pct={projectProgress} />
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground/60 text-center">
                  Summary from EBProjects. Individual time entries are managed in the Projects application.
                </p>
              </>
            )}
          </div>
        </TabsContent>

      </Tabs>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
